import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { formatApiError, getRuntimeProjects, getRuntimeTasks } from '../lib/api';
import ExecutionAgentBuilderPage from './ExecutionAgentBuilderPage';
import { Loader2 } from 'lucide-react';

const initialForm = {
  name: '',
  role: '',
  mission: '',
  taskTypes: '',
  workerType: 'workflow',
  executionPlane: 'vps',
  priority: 'high',
  approvalPolicy: 'human_on_risky_actions',
  checkpointPolicy: 'checkpoint_each_major_step',
  artifactExpectation: 'artifacts_or_successful_tool_executions',
  identitySummary: '',
  personaVibe: '',
  selfPresentation: '',
  userCompany: '',
  userFirstName: '',
  userLastName: '',
  userRoleTitle: '',
  userEmail: '',
  userPhone: '',
  userTimezone: '',
  userPriorities: '',
  userImportantContext: '',
  writingTone: '',
  writingVoice: '',
  writingPreferredLength: 'short_to_medium',
  writingBlacklistPhrases: '',
  externalRequiresApproval: true,
  firstContactRequiresApproval: true,
  bookRequiresApproval: true,
  rescheduleRequiresApproval: true,
  cancelRequiresApproval: true,
  spendingEnabled: false,
  requireApprovalForAnySpend: true,
  maxPerPurchaseCents: '',
  maxPerDayCents: '',
  highRiskRequiresApproval: true,
  destructiveRequiresApproval: true,
};

const getExecutionAgentMetadata = (project) => {
  const metadata = project?.metadata || {};
  if (metadata?.agent_kind === 'agent_employee') return metadata;
  if (metadata?.product_type === 'execution_agent') return metadata;
  return null;
};

const ExecutionAgentEditPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [starterTask, setStarterTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const [projectsRes, tasksRes] = await Promise.all([
          getRuntimeProjects(),
          getRuntimeTasks({ project_id: projectId }),
        ]);
        const selected = (projectsRes.data || []).find((item) => item.id === projectId);
        if (!selected) throw new Error('Execution agent not found');
        setProject(selected);
        setStarterTask((tasksRes.data || [])[0] || null);
      } catch (err) {
        console.error(err);
        setError(formatApiError(err));
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  const initialValues = useMemo(() => {
    const meta = getExecutionAgentMetadata(project) || {};
    const runtimeProfile = meta.runtime_profile || {};
    const agentProfile = runtimeProfile.agent_profile || {};
    const identity = agentProfile.identity || {};
    const aboutUser = agentProfile.about_user || {};
    const writingRules = agentProfile.writing_rules || {};
    const runtimePolicy = runtimeProfile.runtime_policy || {};
    return {
      ...initialForm,
      name: runtimeProfile.name || '',
      role: runtimeProfile.role || '',
      mission: runtimeProfile.mission || project?.description || '',
      taskTypes: Array.isArray(runtimeProfile.task_types) ? runtimeProfile.task_types.join(', ') : '',
      workerType: runtimeProfile.worker_type || 'workflow',
      executionPlane: runtimeProfile.execution_plane || 'vps',
      priority: starterTask?.priority || runtimeProfile.priority || 'high',
      approvalPolicy: runtimeProfile.approval_policy || 'human_on_risky_actions',
      checkpointPolicy: runtimeProfile.checkpoint_policy || 'checkpoint_each_major_step',
      artifactExpectation: runtimeProfile.artifact_expectation || 'artifacts_or_successful_tool_executions',
      identitySummary: identity.identity_summary || '',
      personaVibe: identity.persona_vibe || '',
      selfPresentation: identity.self_presentation || '',
      userCompany: aboutUser.company || '',
      userFirstName: aboutUser.first_name || '',
      userLastName: aboutUser.last_name || '',
      userRoleTitle: aboutUser.role_title || '',
      userEmail: aboutUser.email || '',
      userPhone: aboutUser.phone || '',
      userTimezone: aboutUser.timezone || '',
      userPriorities: Array.isArray(aboutUser.priorities) ? aboutUser.priorities.join('\n') : '',
      userImportantContext: aboutUser.important_context || '',
      writingTone: writingRules.tone || '',
      writingVoice: writingRules.voice || '',
      writingPreferredLength: writingRules.preferred_length || 'short_to_medium',
      writingBlacklistPhrases: Array.isArray(writingRules.blacklisted_phrases) ? writingRules.blacklisted_phrases.join('\n') : '',
      externalRequiresApproval: runtimePolicy.send_policy?.external_requires_approval ?? true,
      firstContactRequiresApproval: runtimePolicy.send_policy?.first_contact_requires_approval ?? true,
      bookRequiresApproval: runtimePolicy.schedule_policy?.book_requires_approval ?? true,
      rescheduleRequiresApproval: runtimePolicy.schedule_policy?.reschedule_requires_approval ?? true,
      cancelRequiresApproval: runtimePolicy.schedule_policy?.cancel_requires_approval ?? true,
      spendingEnabled: runtimePolicy.spend_policy?.spending_enabled ?? false,
      requireApprovalForAnySpend: runtimePolicy.spend_policy?.require_approval_for_any_spend ?? true,
      maxPerPurchaseCents: String(runtimePolicy.spend_policy?.max_per_purchase_cents || ''),
      maxPerDayCents: String(runtimePolicy.spend_policy?.max_per_day_cents || ''),
      highRiskRequiresApproval: runtimePolicy.risk_policy?.high_risk_actions_require_approval ?? true,
      destructiveRequiresApproval: runtimePolicy.risk_policy?.destructive_actions_require_approval ?? true,
      modelPreset: runtimeProfile.model_preset || '',
      customModels: {
        classify: runtimeProfile.models?.classify
          ? { provider: runtimeProfile.models.classify.provider || '', model: runtimeProfile.models.classify.model || '' }
          : { provider: '', model: '' },
        conversational: runtimeProfile.models?.conversational
          ? { provider: runtimeProfile.models.conversational.provider || '', model: runtimeProfile.models.conversational.model || '' }
          : { provider: '', model: '' },
        execution: runtimeProfile.models?.execution
          ? { provider: runtimeProfile.models.execution.provider || '', model: runtimeProfile.models.execution.model || '' }
          : { provider: '', model: '' },
      },
    };
  }, [project, starterTask]);

  if (loading) {
    return <Layout><div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  }

  if (error) {
    return <Layout><div className="p-8"><div className="apollo-card p-4 text-sm text-red-400">{error}</div></div></Layout>;
  }

  return <ExecutionAgentBuilderPage initialValues={initialValues} editMode projectId={projectId} taskId={starterTask?.id || null} />;
};

export default ExecutionAgentEditPage;
